import type { ModelProps, ModelRegistry } from '../types';

// ============================================
// REAL WORLD - VICTORIAN GARDEN
// ============================================

export function HedgeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const leafColor = world === 'real' ? '#228B22' : '#1a4a2a';

  return (
    <group>
      <mesh position={[0, 0.6, 0]} castShadow receiveShadow>
        <boxGeometry args={[2, 1.2, 0.5]} />
        <meshStandardMaterial color={leafColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Leaf texture bumps */}
      {[[-0.6, 0.8], [0, 0.9], [0.6, 0.7], [-0.3, 0.5], [0.4, 0.6]].map(([x, y], i) => (
        <mesh key={i} position={[x, y, 0.26]} castShadow>
          <sphereGeometry args={[0.15, 6, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#2d8b2d' : '#1a5a2a'} roughness={0.95} />
        </mesh>
      ))}
    </group>
  );
}

export function TopiaryModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const leafColor = world === 'real' ? '#228B22' : '#1a4a2a';

  return (
    <group>
      {/* Pot */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.2, 0.4, 8]} />
        <meshStandardMaterial color="#8B4513" roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Trunk */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.06, 0.6, 6]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.9} />
      </mesh>
      {/* Sphere shape */}
      <mesh position={[0, 1.1, 0]} castShadow>
        <sphereGeometry args={[0.35, 12, 12]} />
        <meshStandardMaterial color={leafColor} roughness={0.85} transparent={isOccluding} opacity={opacity} />
      </mesh>
    </group>
  );
}

export function GazeboModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const woodColor = world === 'real' ? '#f5f5f5' : '#d0d0e0';
  const roofColor = world === 'real' ? '#8B0000' : '#5a2a3a';

  return (
    <group>
      {/* Base platform */}
      <mesh position={[0, 0.1, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[1.2, 1.2, 0.2, 8]} />
        <meshStandardMaterial color={woodColor} roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Pillars */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i / 8) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(angle) * 1, 1, Math.sin(angle) * 1]} castShadow>
            <cylinderGeometry args={[0.06, 0.06, 1.8, 8]} />
            <meshStandardMaterial color={woodColor} roughness={0.6} transparent={isOccluding} opacity={opacity} />
          </mesh>
        );
      })}
      {/* Conical roof */}
      <mesh position={[0, 2.2, 0]} castShadow>
        <coneGeometry args={[1.4, 0.8, 8]} />
        <meshStandardMaterial color={roofColor} roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Roof trim */}
      <mesh position={[0, 1.85, 0]}>
        <torusGeometry args={[1.2, 0.05, 8, 24]} />
        <meshStandardMaterial color={woodColor} />
      </mesh>
    </group>
  );
}

export function TeaTableModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const tableColor = world === 'real' ? '#f5f5f5' : '#e0e0f0';

  return (
    <group>
      {/* Table top */}
      <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.6, 0.6, 0.05, 16]} />
        <meshStandardMaterial color={tableColor} roughness={0.4} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Table leg */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.08, 0.5, 8]} />
        <meshStandardMaterial color="#4a4a4a" metalness={0.6} roughness={0.4} />
      </mesh>
      {/* Table base */}
      <mesh position={[0, 0.02, 0]} castShadow>
        <cylinderGeometry args={[0.3, 0.3, 0.04, 8]} />
        <meshStandardMaterial color="#4a4a4a" metalness={0.6} roughness={0.4} />
      </mesh>
      {/* Teapot */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color="#e8e8ff" roughness={0.3} />
      </mesh>
      {/* Teapot lid */}
      <mesh position={[0, 0.7, 0]} castShadow>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color="#d8d8ff" roughness={0.3} />
      </mesh>
      {/* Teacups */}
      {[[0.25, 0.1], [-0.2, 0.15], [0.1, -0.2]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.55, z]} castShadow>
          <cylinderGeometry args={[0.04, 0.03, 0.05, 8]} />
          <meshStandardMaterial color={['#ffcccc', '#ccffcc', '#ccccff'][i]} roughness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

export function RoseBushModel({ world, properties, isOccluding, opacity = 1 }: ModelProps) {
  const color = properties?.color as string;
  const isBloodRed = color === 'blood_red';
  const isWhite = color === 'white';
  const flowerColor = isBloodRed ? '#8B0000' : (isWhite ? '#fff0f0' : '#ff3366');
  const leafColor = world === 'real' ? '#1a5a1a' : '#0d3d0d';
  // Add glow to make roses more visible
  const emissiveColor = isBloodRed ? '#4a0000' : (isWhite ? '#ffcccc' : '#ff1744');
  const emissiveIntensity = isBloodRed ? 0.4 : 0.3;

  return (
    <group>
      {/* Bush base - larger and darker for contrast */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <dodecahedronGeometry args={[0.6, 1]} />
        <meshStandardMaterial color={leafColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* More leaves for volume */}
      <mesh position={[0.3, 0.35, 0.2]} castShadow>
        <dodecahedronGeometry args={[0.35, 0]} />
        <meshStandardMaterial color={leafColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.25, 0.3, -0.2]} castShadow>
        <dodecahedronGeometry args={[0.3, 0]} />
        <meshStandardMaterial color={leafColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Roses - LARGER and more prominent with glow */}
      {[
        [0.4, 0.85, 0.25], [-0.3, 0.95, 0.35], [0.15, 0.75, -0.35],
        [-0.35, 0.65, -0.2], [0.3, 0.9, -0.15], [0, 1.0, 0.1],
        [-0.1, 0.7, 0.4], [0.25, 0.6, 0.3]
      ].map(([x, y, z], i) => (
        <group key={i} position={[x, y, z]}>
          {/* Rose center - much larger */}
          <mesh castShadow>
            <sphereGeometry args={[0.12, 12, 12]} />
            <meshStandardMaterial
              color={flowerColor}
              roughness={0.4}
              emissive={emissiveColor}
              emissiveIntensity={emissiveIntensity}
            />
          </mesh>
          {/* Inner petals - spiral pattern */}
          {[0, 1, 2, 3, 4, 5].map((j) => {
            const angle = (j / 6) * Math.PI * 2;
            const radius = 0.08;
            return (
              <mesh key={j} position={[Math.cos(angle) * radius, 0.02, Math.sin(angle) * radius]} rotation={[0.4, angle, 0.2]}>
                <planeGeometry args={[0.08, 0.1]} />
                <meshStandardMaterial
                  color={flowerColor}
                  side={2}
                  emissive={emissiveColor}
                  emissiveIntensity={emissiveIntensity * 0.5}
                />
              </mesh>
            );
          })}
          {/* Outer petals - larger */}
          {[0, 1, 2, 3, 4].map((j) => {
            const angle = (j / 5) * Math.PI * 2 + 0.3;
            const radius = 0.12;
            return (
              <mesh key={`outer-${j}`} position={[Math.cos(angle) * radius, -0.03, Math.sin(angle) * radius]} rotation={[0.6, angle, 0]}>
                <planeGeometry args={[0.1, 0.12]} />
                <meshStandardMaterial
                  color={flowerColor}
                  side={2}
                  emissive={emissiveColor}
                  emissiveIntensity={emissiveIntensity * 0.3}
                />
              </mesh>
            );
          })}
        </group>
      ))}
      {/* Blood drips for spectral version */}
      {isBloodRed && world === 'spectral' && (
        <>
          <mesh position={[0.25, 0.4, 0.15]}>
            <sphereGeometry args={[0.03, 6, 6]} />
            <meshStandardMaterial color="#8B0000" emissive="#660000" emissiveIntensity={0.6} />
          </mesh>
          <mesh position={[-0.2, 0.35, 0.25]}>
            <sphereGeometry args={[0.025, 6, 6]} />
            <meshStandardMaterial color="#8B0000" emissive="#660000" emissiveIntensity={0.6} />
          </mesh>
          <mesh position={[0.1, 0.3, -0.2]}>
            <sphereGeometry args={[0.02, 6, 6]} />
            <meshStandardMaterial color="#8B0000" emissive="#660000" emissiveIntensity={0.6} />
          </mesh>
        </>
      )}
    </group>
  );
}

export function SundialModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const stoneColor = world === 'real' ? '#d0d0d0' : '#a0a0b0';

  return (
    <group>
      {/* Pedestal */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.2, 0.6, 8]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Dial face */}
      <mesh position={[0, 0.62, 0]} rotation={[-0.2, 0, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.25, 0.04, 16]} />
        <meshStandardMaterial color={stoneColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Gnomon (shadow caster) */}
      <mesh position={[0, 0.72, -0.05]} rotation={[-0.5, 0, 0]} castShadow>
        <boxGeometry args={[0.01, 0.15, 0.08]} />
        <meshStandardMaterial color="#4a4a4a" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

// ============================================
// SPECTRAL WORLD - WONDERLAND TWISTED
// ============================================

export function RabbitHoleModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Hole opening */}
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0, 0.5, 16]} />
        <meshStandardMaterial
          color="#0a0a1a"
          emissive={world === 'spectral' ? '#2a1a4a' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.3 : 0}
        />
      </mesh>
      {/* Dirt mound around */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <torusGeometry args={[0.5, 0.15, 8, 16]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Roots */}
      {[0, 1, 2, 3].map((i) => {
        const angle = (i / 4) * Math.PI * 2 + 0.3;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.4, 0.1, Math.sin(angle) * 0.4]} rotation={[0.5, angle, 0.3]} castShadow>
            <cylinderGeometry args={[0.03, 0.02, 0.25, 5]} />
            <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
          </mesh>
        );
      })}
      {/* Mysterious glow from within */}
      {world === 'spectral' && (
        <pointLight position={[0, -0.3, 0]} color="#8866ff" intensity={2} distance={3} />
      )}
    </group>
  );
}

export function MadTeaTableModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Long crooked table */}
      <mesh position={[0, 0.5, 0]} rotation={[0, 0, 0.05]} castShadow receiveShadow>
        <boxGeometry args={[2.5, 0.08, 0.8]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#6a4a3a' : '#8B4513'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Crooked legs */}
      {[[-1, -0.3], [-0.3, 0.3], [0.5, -0.2], [1.1, 0.25]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.25, z]} rotation={[0.1 * (i % 2 === 0 ? 1 : -1), 0, 0.1 * (i % 2)]} castShadow>
          <cylinderGeometry args={[0.04, 0.05, 0.5, 6]} />
          <meshStandardMaterial color="#5a3a2a" roughness={0.9} />
        </mesh>
      ))}
      {/* Scattered teacups and teapots */}
      {[[-0.8, 0.55, 0.1], [0.2, 0.58, -0.2], [0.9, 0.56, 0.15], [-0.3, 0.57, 0.25]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]} rotation={[0, i * 0.8, (i % 2) * 0.2]} castShadow>
          <cylinderGeometry args={[0.06, 0.04, 0.08, 8]} />
          <meshStandardMaterial
            color={['#ff88aa', '#88ffaa', '#88aaff', '#ffaa88'][i]}
            emissive={['#ff44aa', '#44ff88', '#4488ff', '#ff8844'][i]}
            emissiveIntensity={0.2}
          />
        </mesh>
      ))}
      {/* Floating teapot */}
      <group position={[0.5, 1, 0]}>
        <mesh castShadow>
          <sphereGeometry args={[0.12, 10, 10]} />
          <meshStandardMaterial color="#aa66ff" emissive="#8844ff" emissiveIntensity={0.4} />
        </mesh>
        {/* Spout */}
        <mesh position={[0.12, 0, 0]} rotation={[0, 0, -0.5]}>
          <cylinderGeometry args={[0.02, 0.03, 0.1, 6]} />
          <meshStandardMaterial color="#9955ee" />
        </mesh>
      </group>
      {/* Chairs (tilted) */}
      {[[-0.8, 0.3, -0.6], [0, 0.3, -0.7], [0.8, 0.25, -0.5]].map(([x, y, z], i) => (
        <group key={i} position={[x, y, z]} rotation={[0, i * 0.5, 0.1 * (i - 1)]}>
          <mesh castShadow>
            <boxGeometry args={[0.25, 0.05, 0.25]} />
            <meshStandardMaterial color="#6a4a3a" />
          </mesh>
          <mesh position={[0, 0.2, -0.1]}>
            <boxGeometry args={[0.25, 0.35, 0.05]} />
            <meshStandardMaterial color="#6a4a3a" />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export function GiantMushroomModel({ world, properties, isOccluding, opacity = 1 }: ModelProps) {
  const colorProp = properties?.color as string;
  const capColor = colorProp === 'blue' ? '#4169E1' : (colorProp === 'purple' ? '#8B008B' : '#FF4500');
  const spotColor = colorProp === 'blue' ? '#87CEEB' : (colorProp === 'purple' ? '#DDA0DD' : '#FFD700');

  return (
    <group>
      {/* Stem */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.25, 1, 8]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#e8e0d0' : '#f5f0e8'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Cap */}
      <mesh position={[0, 1.1, 0]} castShadow>
        <sphereGeometry args={[0.5, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={capColor}
          roughness={0.6}
          emissive={world === 'spectral' ? capColor : undefined}
          emissiveIntensity={world === 'spectral' ? 0.3 : 0}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Spots */}
      {[[0.2, 1.2, 0.35], [-0.25, 1.15, 0.3], [0.3, 1.1, -0.2], [-0.15, 1.25, -0.25], [0.05, 1.3, 0.15]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <circleGeometry args={[0.08 - i * 0.01, 8]} />
          <meshStandardMaterial
            color={spotColor}
            side={2}
            emissive={world === 'spectral' ? spotColor : undefined}
            emissiveIntensity={world === 'spectral' ? 0.5 : 0}
          />
        </mesh>
      ))}
      {/* Glow underneath */}
      {world === 'spectral' && (
        <pointLight position={[0, 0.8, 0]} color={capColor} intensity={2} distance={3} />
      )}
    </group>
  );
}

export function QueenCastleModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const wallColor = world === 'spectral' ? '#8B0000' : '#cc4444';
  const heartColor = '#ff0000';

  return (
    <group>
      {/* Main tower */}
      <mesh position={[0, 1.2, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.2, 2.4, 1.2]} />
        <meshStandardMaterial color={wallColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Heart decorations on walls */}
      {[[0, 1.5, 0.61], [0.61, 1.5, 0], [-0.61, 1.5, 0], [0, 1.5, -0.61]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]} rotation={[0, i * Math.PI / 2, 0]}>
          <planeGeometry args={[0.4, 0.4]} />
          <meshStandardMaterial
            color={heartColor}
            emissive={world === 'spectral' ? '#aa0000' : undefined}
            emissiveIntensity={world === 'spectral' ? 0.5 : 0}
          />
        </mesh>
      ))}
      {/* Corner towers */}
      {[[-0.5, 0.5], [0.5, 0.5], [-0.5, -0.5], [0.5, -0.5]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 1.4, 0]} castShadow>
            <cylinderGeometry args={[0.25, 0.3, 2.8, 8]} />
            <meshStandardMaterial color={wallColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
          </mesh>
          {/* Cone top shaped like heart */}
          <mesh position={[0, 3, 0]} castShadow>
            <coneGeometry args={[0.35, 0.6, 8]} />
            <meshStandardMaterial color={heartColor} roughness={0.6} />
          </mesh>
        </group>
      ))}
      {/* Battlements */}
      {[-0.4, 0, 0.4].map((x, i) => (
        <mesh key={i} position={[x, 2.5, 0.5]} castShadow>
          <boxGeometry args={[0.2, 0.3, 0.15]} />
          <meshStandardMaterial color={wallColor} roughness={0.8} />
        </mesh>
      ))}
      {/* Gate */}
      <mesh position={[0, 0.5, 0.61]}>
        <boxGeometry args={[0.4, 1, 0.1]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
    </group>
  );
}

export function CardSoldierModel({ world, properties, isOccluding, opacity = 1 }: ModelProps) {
  const suit = properties?.suit as string;
  const suitColor = suit === 'hearts' ? '#ff0000' : '#000000';
  const cardColor = '#f8f8f0';

  return (
    <group>
      {/* Card body - flat rectangle */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <boxGeometry args={[0.6, 1.5, 0.05]} />
        <meshStandardMaterial color={cardColor} roughness={0.4} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Suit symbol */}
      <mesh position={[0, 1, 0.03]}>
        <planeGeometry args={[0.3, 0.3]} />
        <meshStandardMaterial color={suitColor} />
      </mesh>
      {/* Face (simple) */}
      <mesh position={[0, 1.3, 0.03]}>
        <circleGeometry args={[0.15, 12]} />
        <meshStandardMaterial color="#ffe4c4" />
      </mesh>
      {/* Eyes */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 1.35, 0.04]}>
          <circleGeometry args={[0.02, 8]} />
          <meshBasicMaterial color="#000000" />
        </mesh>
      ))}
      {/* Legs */}
      {[-0.15, 0.15].map((x, i) => (
        <mesh key={i} position={[x, 0.15, 0]} castShadow>
          <boxGeometry args={[0.1, 0.3, 0.05]} />
          <meshStandardMaterial color={suitColor} />
        </mesh>
      ))}
      {/* Spear */}
      <mesh position={[0.4, 0.9, 0]} rotation={[0, 0, 0.1]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 1.5, 6]} />
        <meshStandardMaterial color="#4a4a4a" metalness={0.5} />
      </mesh>
      <mesh position={[0.4, 1.7, 0]} castShadow>
        <coneGeometry args={[0.05, 0.15, 4]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.7} />
      </mesh>
    </group>
  );
}

export function BrokenClockModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group rotation={[0, 0, 0.15]}>
      {/* Clock body */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <cylinderGeometry args={[0.4, 0.35, 0.8, 12]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#6a4a3a' : '#8B4513'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Clock face */}
      <mesh position={[0, 0.8, 0.18]}>
        <circleGeometry args={[0.3, 24]} />
        <meshStandardMaterial color="#f8f8e8" />
      </mesh>
      {/* Clock hands - stuck at 6 o'clock */}
      <mesh position={[0, 0.7, 0.19]} rotation={[0, 0, 0]}>
        <boxGeometry args={[0.02, 0.2, 0.01]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0, 0.65, 0.19]} rotation={[0, 0, Math.PI]}>
        <boxGeometry args={[0.015, 0.12, 0.01]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Crack */}
      <mesh position={[0.1, 0.9, 0.19]} rotation={[0, 0, 0.5]}>
        <boxGeometry args={[0.02, 0.25, 0.005]} />
        <meshStandardMaterial color="#3a3a3a" />
      </mesh>
      {/* Pendulum (stopped) */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <cylinderGeometry args={[0.01, 0.01, 0.3, 6]} />
        <meshStandardMaterial color="#c0a050" metalness={0.6} />
      </mesh>
      <mesh position={[0, 0.15, 0]} castShadow>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color="#c0a050" metalness={0.7} />
      </mesh>
      {/* Eerie glow */}
      {world === 'spectral' && (
        <pointLight position={[0, 0.8, 0.3]} color="#ff66aa" intensity={1.5} distance={2} />
      )}
    </group>
  );
}

export function FlyingTeacupModel({ world }: ModelProps) {
  return (
    <group>
      {/* Cup */}
      <mesh castShadow>
        <cylinderGeometry args={[0.12, 0.08, 0.12, 12]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#ff88cc' : '#e8e8ff'}
          roughness={0.3}
          emissive={world === 'spectral' ? '#ff44aa' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.3 : 0}
        />
      </mesh>
      {/* Handle */}
      <mesh position={[0.14, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <torusGeometry args={[0.05, 0.015, 8, 12, Math.PI]} />
        <meshStandardMaterial color={world === 'spectral' ? '#ff88cc' : '#e8e8ff'} />
      </mesh>
      {/* Tea inside (glowing in spectral) */}
      <mesh position={[0, 0.04, 0]}>
        <cylinderGeometry args={[0.1, 0.1, 0.02, 12]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#88ff88' : '#aa8855'}
          emissive={world === 'spectral' ? '#44ff44' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.5 : 0}
        />
      </mesh>
      {/* Steam/magic particles */}
      {world === 'spectral' && [0.03, -0.02, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.1 + i * 0.05, 0]}>
          <sphereGeometry args={[0.015, 6, 6]} />
          <meshStandardMaterial color="#ffffff" emissive="#aaffaa" emissiveIntensity={1} transparent opacity={0.6} />
        </mesh>
      ))}
    </group>
  );
}

export function StrangeDoorModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Door frame */}
      <mesh position={[-0.45, 0.9, 0]} castShadow>
        <boxGeometry args={[0.1, 1.8, 0.15]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.45, 0.9, 0]} castShadow>
        <boxGeometry args={[0.1, 1.8, 0.15]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 1.85, 0]} castShadow>
        <boxGeometry args={[1, 0.1, 0.15]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Door itself - warped */}
      <mesh position={[0, 0.9, 0.05]} rotation={[0, 0.1, 0.05]} castShadow>
        <boxGeometry args={[0.8, 1.7, 0.08]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#8866aa' : '#6a4a3a'}
          roughness={0.7}
          emissive={world === 'spectral' ? '#4422aa' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.3 : 0}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Keyhole (glowing) */}
      <mesh position={[0.25, 0.8, 0.1]}>
        <circleGeometry args={[0.04, 8]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#ffcc00' : '#3a3a3a'}
          emissive={world === 'spectral' ? '#ffaa00' : undefined}
          emissiveIntensity={world === 'spectral' ? 1 : 0}
        />
      </mesh>
      {/* Mysterious light through keyhole */}
      {world === 'spectral' && (
        <pointLight position={[0.25, 0.8, 0.2]} color="#ffcc00" intensity={2} distance={2} />
      )}
    </group>
  );
}

export function PocketWatchModel({ world }: ModelProps) {
  return (
    <group rotation={[0.3, 0, 0]}>
      {/* Watch body */}
      <mesh castShadow>
        <cylinderGeometry args={[0.12, 0.12, 0.03, 16]} />
        <meshStandardMaterial
          color="#c0a050"
          metalness={0.8}
          roughness={0.2}
          emissive={world === 'spectral' ? '#806020' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.3 : 0}
        />
      </mesh>
      {/* Watch face */}
      <mesh position={[0, 0.016, 0]}>
        <circleGeometry args={[0.1, 16]} />
        <meshStandardMaterial color="#f8f8e8" />
      </mesh>
      {/* Hands */}
      <mesh position={[0, 0.02, 0]}>
        <boxGeometry args={[0.01, 0.06, 0.005]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0, 0.02, 0]} rotation={[0, 0, Math.PI / 3]}>
        <boxGeometry args={[0.008, 0.08, 0.005]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Chain loop */}
      <mesh position={[0, 0, -0.13]}>
        <torusGeometry args={[0.025, 0.008, 8, 12]} />
        <meshStandardMaterial color="#c0a050" metalness={0.8} />
      </mesh>
    </group>
  );
}

export function PotionBottleModel({ world }: ModelProps) {
  return (
    <group>
      {/* Bottle body */}
      <mesh position={[0, 0.12, 0]} castShadow>
        <cylinderGeometry args={[0.06, 0.08, 0.24, 8]} />
        <meshStandardMaterial
          color="#88aaff"
          transparent
          opacity={0.7}
          emissive={world === 'spectral' ? '#4488ff' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.5 : 0}
        />
      </mesh>
      {/* Neck */}
      <mesh position={[0, 0.28, 0]} castShadow>
        <cylinderGeometry args={[0.03, 0.04, 0.1, 8]} />
        <meshStandardMaterial color="#88aaff" transparent opacity={0.7} />
      </mesh>
      {/* Cork */}
      <mesh position={[0, 0.36, 0]} castShadow>
        <cylinderGeometry args={[0.035, 0.03, 0.06, 8]} />
        <meshStandardMaterial color="#8B4513" roughness={0.9} />
      </mesh>
      {/* Liquid inside */}
      <mesh position={[0, 0.1, 0]}>
        <cylinderGeometry args={[0.055, 0.075, 0.18, 8]} />
        <meshStandardMaterial
          color="#ff66aa"
          transparent
          opacity={0.8}
          emissive="#ff44aa"
          emissiveIntensity={0.5}
        />
      </mesh>
      {/* Label "DRINK ME" */}
      <mesh position={[0, 0.12, 0.065]}>
        <planeGeometry args={[0.08, 0.04]} />
        <meshStandardMaterial color="#f8f0e0" />
      </mesh>
      {/* Glow */}
      {world === 'spectral' && (
        <pointLight position={[0, 0.15, 0]} color="#ff66aa" intensity={1.5} distance={1.5} />
      )}
    </group>
  );
}

// ==================== ADDITIONAL MODELS ====================

// Thought Bubble - for anxiety thoughts
function ThoughtBubbleModel({ world, properties }: ModelProps) {
  const isSpectral = world === 'spectral';
  const bubbleColor = (properties?.color as string) || (isSpectral ? '#ff4444' : '#ffffff');
  const text = (properties?.text as string) || '';

  return (
    <group>
      {/* Main bubble */}
      <mesh castShadow>
        <sphereGeometry args={[0.3, 16, 16]} />
        <meshStandardMaterial
          color={bubbleColor}
          transparent
          opacity={0.7}
          emissive={bubbleColor}
          emissiveIntensity={isSpectral ? 0.4 : 0.1}
        />
      </mesh>
      {/* Thought trail */}
      <mesh position={[0, -0.35, 0]} castShadow>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshStandardMaterial color={bubbleColor} transparent opacity={0.5} />
      </mesh>
      <mesh position={[0, -0.5, 0]} castShadow>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color={bubbleColor} transparent opacity={0.4} />
      </mesh>
      {/* Inner glow for spectral */}
      {isSpectral && (
        <pointLight position={[0, 0, 0]} color={bubbleColor} intensity={0.8} distance={1} />
      )}
    </group>
  );
}

// Photo Frame - for memory photos
function PhotoFrameModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const frameColor = isSpectral ? '#8b7355' : '#daa520';
  const photoColor = isSpectral ? '#a08070' : '#f5deb3';

  return (
    <group>
      {/* Frame */}
      <mesh castShadow rotation={[-0.2, 0, 0]}>
        <boxGeometry args={[0.4, 0.5, 0.03]} />
        <meshStandardMaterial color={frameColor} metalness={0.3} roughness={0.7} />
      </mesh>
      {/* Photo area */}
      <mesh position={[0, 0, 0.02]} rotation={[-0.2, 0, 0]} castShadow>
        <planeGeometry args={[0.32, 0.4]} />
        <meshStandardMaterial color={photoColor} />
      </mesh>
      {/* Silhouettes on photo */}
      <mesh position={[-0.06, 0.05, 0.025]} rotation={[-0.2, 0, 0]} castShadow>
        <circleGeometry args={[0.05, 8]} />
        <meshStandardMaterial color={isSpectral ? '#5a4a3a' : '#8b7355'} />
      </mesh>
      <mesh position={[0.06, 0.05, 0.025]} rotation={[-0.2, 0, 0]} castShadow>
        <circleGeometry args={[0.05, 8]} />
        <meshStandardMaterial color={isSpectral ? '#5a4a3a' : '#8b7355'} />
      </mesh>
      {/* Glow when hovered */}
      {hovered && (
        <pointLight position={[0, 0, 0.1]} color="#ffd700" intensity={0.5} distance={1} />
      )}
    </group>
  );
}

// Scroll - for letters and notes
function ScrollModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const paperColor = isSpectral ? '#c8b8a8' : '#f5e6c8';
  const rollColor = isSpectral ? '#6a5a4a' : '#8b7355';

  return (
    <group>
      {/* Rolled ends */}
      <mesh position={[0, 0.12, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 0.35, 12]} />
        <meshStandardMaterial color={rollColor} />
      </mesh>
      <mesh position={[0, -0.08, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 0.35, 12]} />
        <meshStandardMaterial color={rollColor} />
      </mesh>
      {/* Paper */}
      <mesh position={[0, 0.02, 0.01]} castShadow>
        <planeGeometry args={[0.3, 0.16]} />
        <meshStandardMaterial color={paperColor} side={2} />
      </mesh>
      {/* Text lines */}
      {[-0.04, 0, 0.04].map((y, i) => (
        <mesh key={i} position={[0, 0.02 + y, 0.015]} castShadow>
          <planeGeometry args={[0.22, 0.015]} />
          <meshStandardMaterial color={isSpectral ? '#4a3a2a' : '#2a1a0a'} />
        </mesh>
      ))}
      {/* Glow when hovered */}
      {hovered && (
        <pointLight position={[0, 0, 0.1]} color="#ffd700" intensity={0.4} distance={1} />
      )}
    </group>
  );
}

// Teacup - for tea party
function TeacupModel({ world, hovered, properties }: ModelProps) {
  const isSpectral = world === 'spectral';
  const cupColor = isSpectral ? '#d8d8e8' : '#f5f5dc';
  const decorColor = isSpectral ? '#6a5aaa' : '#4169e1';
  const teaColor = isSpectral ? '#4a3a2a' : '#8b4513';

  return (
    <group>
      {/* Cup body */}
      <mesh position={[0, 0.08, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.07, 0.12, 16]} />
        <meshStandardMaterial color={cupColor} />
      </mesh>
      {/* Cup rim */}
      <mesh position={[0, 0.14, 0]} castShadow>
        <torusGeometry args={[0.1, 0.01, 8, 16]} />
        <meshStandardMaterial color={cupColor} />
      </mesh>
      {/* Handle */}
      <mesh position={[0.12, 0.08, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <torusGeometry args={[0.05, 0.015, 8, 16, Math.PI]} />
        <meshStandardMaterial color={cupColor} />
      </mesh>
      {/* Decorative pattern */}
      <mesh position={[0, 0.08, 0.08]} castShadow>
        <circleGeometry args={[0.025, 8]} />
        <meshStandardMaterial color={decorColor} />
      </mesh>
      {/* Tea inside */}
      <mesh position={[0, 0.12, 0]} rotation={[-Math.PI / 2, 0, 0]} castShadow>
        <circleGeometry args={[0.08, 16]} />
        <meshStandardMaterial color={teaColor} />
      </mesh>
      {/* Saucer */}
      <mesh position={[0, 0.01, 0]} castShadow>
        <cylinderGeometry args={[0.14, 0.14, 0.02, 16]} />
        <meshStandardMaterial color={cupColor} />
      </mesh>
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.1, 0]} color="#ffd700" intensity={0.3} distance={0.8} />
      )}
    </group>
  );
}

// Small mushroom - decorative
function SmallMushroomModel({ world, properties }: ModelProps) {
  const isSpectral = world === 'spectral';
  const capColor = (properties?.color as string) || (isSpectral ? '#9370db' : '#ff6b6b');
  const stemColor = isSpectral ? '#c8c8d8' : '#f5f5dc';

  return (
    <group>
      {/* Stem */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.06, 0.2, 8]} />
        <meshStandardMaterial color={stemColor} />
      </mesh>
      {/* Cap */}
      <mesh position={[0, 0.22, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={capColor} />
      </mesh>
      {/* Spots */}
      {[[0.06, 0.25, 0.06], [-0.04, 0.27, 0.08], [0.02, 0.28, -0.06]].map((pos, i) => (
        <mesh key={i} position={pos as [number, number, number]} castShadow>
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
      ))}
    </group>
  );
}

// Twisted Hedge - spectral version
function HedgeTwistedModel({ world }: ModelProps) {
  const isSpectral = world === 'spectral';
  const leafColor = isSpectral ? '#2a3a2a' : '#3a5a3a';

  return (
    <group>
      {/* Twisted main body */}
      <mesh position={[0, 0.6, 0]} rotation={[0.1, 0.2, 0.15]} castShadow>
        <boxGeometry args={[0.8, 1.2, 0.4]} />
        <meshStandardMaterial color={leafColor} />
      </mesh>
      {/* Twisted branches */}
      <mesh position={[0.3, 0.9, 0.1]} rotation={[0.3, 0.5, 0.2]} castShadow>
        <boxGeometry args={[0.3, 0.5, 0.2]} />
        <meshStandardMaterial color={leafColor} />
      </mesh>
      <mesh position={[-0.25, 0.8, -0.1]} rotation={[-0.2, -0.3, 0.1]} castShadow>
        <boxGeometry args={[0.25, 0.4, 0.2]} />
        <meshStandardMaterial color={leafColor} />
      </mesh>
      {/* Thorns */}
      {isSpectral && (
        <>
          {[[0.4, 0.7, 0.2], [-0.3, 0.5, 0.2], [0.2, 1.0, -0.15]].map((pos, i) => (
            <mesh key={i} position={pos as [number, number, number]} rotation={[Math.random(), Math.random(), Math.random()]} castShadow>
              <coneGeometry args={[0.03, 0.12, 4]} />
              <meshStandardMaterial color="#1a1a1a" />
            </mesh>
          ))}
        </>
      )}
    </group>
  );
}

// ==================== TEA PARTY COLLECTIBLE ITEMS ====================

// Collectible Teacup - for tea party puzzle
export function TeaCupCollectibleModel({ world, hovered, properties }: ModelProps) {
  const color = (properties?.color as string) || '#FFB6C1';
  const glows = properties?.glows as boolean;

  return (
    <group>
      {/* Cup body */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.12, 0.22, 16]} />
        <meshStandardMaterial
          color="#f5f5f5"
          emissive={glows ? color : undefined}
          emissiveIntensity={hovered ? 0.5 : 0.2}
        />
      </mesh>
      {/* Cup rim (gold) */}
      <mesh position={[0, 0.26, 0]} castShadow>
        <torusGeometry args={[0.18, 0.015, 8, 24]} />
        <meshStandardMaterial color="#daa520" metalness={0.6} />
      </mesh>
      {/* Handle */}
      <mesh position={[0.22, 0.15, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <torusGeometry args={[0.08, 0.025, 8, 16, Math.PI]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
      {/* Tea inside */}
      <mesh position={[0, 0.22, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.15, 16]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      {/* Decorative heart */}
      <mesh position={[0, 0.15, 0.13]}>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} />
      </mesh>
      {/* Glow */}
      {hovered && <pointLight position={[0, 0.2, 0]} color={color} intensity={1} distance={1.5} />}
    </group>
  );
}

// Collectible Saucer - for tea party puzzle
export function TeaSaucerCollectibleModel({ world, hovered, properties }: ModelProps) {
  const color = (properties?.color as string) || '#98FB98';
  const glows = properties?.glows as boolean;

  return (
    <group>
      {/* Main saucer plate */}
      <mesh position={[0, 0.03, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.25, 0.04, 24]} />
        <meshStandardMaterial
          color="#f5f5f5"
          emissive={glows ? color : undefined}
          emissiveIntensity={hovered ? 0.5 : 0.2}
        />
      </mesh>
      {/* Raised center for cup */}
      <mesh position={[0, 0.06, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.14, 0.02, 16]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
      {/* Decorative rim (gold) */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <torusGeometry args={[0.24, 0.01, 8, 32]} />
        <meshStandardMaterial color="#daa520" metalness={0.6} />
      </mesh>
      {/* Inner decorative ring */}
      <mesh position={[0, 0.055, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.16, 0.18, 24]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.2} />
      </mesh>
      {/* Glow */}
      {hovered && <pointLight position={[0, 0.1, 0]} color={color} intensity={1} distance={1.5} />}
    </group>
  );
}

// Collectible Teapot - for tea party puzzle
export function TeapotCollectibleModel({ world, hovered, properties }: ModelProps) {
  const color = (properties?.color as string) || '#87CEEB';
  const glows = properties?.glows as boolean;

  return (
    <group>
      {/* Main body */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshStandardMaterial
          color="#f5f5f5"
          emissive={glows ? color : undefined}
          emissiveIntensity={hovered ? 0.5 : 0.2}
        />
      </mesh>
      {/* Lid */}
      <mesh position={[0, 0.38, 0]} castShadow>
        <sphereGeometry args={[0.1, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
      {/* Lid knob */}
      <mesh position={[0, 0.45, 0]} castShadow>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color="#daa520" metalness={0.6} />
      </mesh>
      {/* Spout */}
      <mesh position={[0.22, 0.22, 0]} rotation={[0, 0, -0.6]} castShadow>
        <cylinderGeometry args={[0.03, 0.05, 0.2, 8]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
      {/* Handle */}
      <mesh position={[-0.22, 0.22, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
        <torusGeometry args={[0.1, 0.025, 8, 16, Math.PI]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
      {/* Decorative band */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <torusGeometry args={[0.2, 0.015, 8, 24]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} />
      </mesh>
      {/* Steam wisps */}
      {[[-0.02, 0.5, 0], [0.03, 0.55, 0.02], [0, 0.6, -0.02]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <sphereGeometry args={[0.02 - i * 0.005, 6, 6]} />
          <meshStandardMaterial color="#ffffff" transparent opacity={0.5 - i * 0.1} />
        </mesh>
      ))}
      {/* Glow */}
      {hovered && <pointLight position={[0, 0.25, 0]} color={color} intensity={1} distance={1.5} />}
    </group>
  );
}

// Collectible Cake - for tea party puzzle
export function TeaCakeCollectibleModel({ world, hovered, properties }: ModelProps) {
  const color = (properties?.color as string) || '#DDA0DD';
  const glows = properties?.glows as boolean;

  return (
    <group>
      {/* Cake plate */}
      <mesh position={[0, 0.02, 0]} castShadow>
        <cylinderGeometry args={[0.22, 0.22, 0.03, 16]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
      {/* Bottom layer */}
      <mesh position={[0, 0.12, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.18, 0.15, 16]} />
        <meshStandardMaterial
          color="#ffdab9"
          emissive={glows ? color : undefined}
          emissiveIntensity={hovered ? 0.4 : 0.15}
        />
      </mesh>
      {/* Frosting layer */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.19, 0.18, 0.02, 16]} />
        <meshStandardMaterial color="#ffe4e1" />
      </mesh>
      {/* Top layer */}
      <mesh position={[0, 0.28, 0]} castShadow>
        <cylinderGeometry args={[0.14, 0.14, 0.12, 16]} />
        <meshStandardMaterial color="#ffdab9" />
      </mesh>
      {/* Top frosting */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.14, 0.02, 16]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} />
      </mesh>
      {/* Cherry on top */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color="#ff0040" emissive="#ff0040" emissiveIntensity={0.3} />
      </mesh>
      {/* Cherry stem */}
      <mesh position={[0, 0.44, 0]} castShadow>
        <cylinderGeometry args={[0.005, 0.005, 0.06, 4]} />
        <meshStandardMaterial color="#228b22" />
      </mesh>
      {/* Decorative swirls */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.12, 0.36, Math.sin(angle) * 0.12]} castShadow>
            <sphereGeometry args={[0.025, 6, 6]} />
            <meshStandardMaterial color="#ffe4e1" />
          </mesh>
        );
      })}
      {/* Glow */}
      {hovered && <pointLight position={[0, 0.25, 0]} color={color} intensity={1} distance={1.5} />}
    </group>
  );
}

// Placed item indicator - shows item on tile after placement
export function PlacedTeaItemModel({ world, properties }: ModelProps) {
  const itemType = (properties?.itemType as string) || 'cup';
  const color = (properties?.color as string) || '#ffffff';

  // Render the appropriate item based on type
  const ItemComponent = {
    cup: TeaCupCollectibleModel,
    saucer: TeaSaucerCollectibleModel,
    teapot: TeapotCollectibleModel,
    cake: TeaCakeCollectibleModel,
  }[itemType] || TeaCupCollectibleModel;

  return (
    <group>
      <ItemComponent world={world} properties={{ color }} />
      {/* Success sparkles */}
      {[0, 1, 2, 3].map((i) => {
        const angle = (i / 4) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.3, 0.3 + Math.sin(i) * 0.1, Math.sin(angle) * 0.3]}>
            <sphereGeometry args={[0.02, 6, 6]} />
            <meshStandardMaterial color="#ffd700" emissive="#ffd700" emissiveIntensity={1} transparent opacity={0.8} />
          </mesh>
        );
      })}
    </group>
  );
}

// Registry of all Alice in Wonderland models
export const wonderlandModels: ModelRegistry = {
  // Tea Party Collectibles
  tea_cup: TeaCupCollectibleModel,
  tea_saucer: TeaSaucerCollectibleModel,
  tea_teapot: TeapotCollectibleModel,
  tea_cake: TeaCakeCollectibleModel,
  placed_tea_item: PlacedTeaItemModel,
  // Victorian Garden (Real)
  hedge: HedgeModel,
  topiary: TopiaryModel,
  gazebo: GazeboModel,
  tea_table: TeaTableModel,
  rose_bush: RoseBushModel,
  sundial: SundialModel,
  // Wonderland Twisted (Spectral)
  rabbit_hole: RabbitHoleModel,
  mad_tea_table: MadTeaTableModel,
  giant_mushroom: GiantMushroomModel,
  queen_castle: QueenCastleModel,
  card_soldier: CardSoldierModel,
  broken_clock: BrokenClockModel,
  flying_teacup: FlyingTeacupModel,
  strange_door: StrangeDoorModel,
  // Collectibles
  pocket_watch: PocketWatchModel,
  potion_bottle: PotionBottleModel,
  // Additional models
  thought_bubble: ThoughtBubbleModel,
  photo_frame: PhotoFrameModel,
  scroll: ScrollModel,
  teacup: TeacupModel,
  mushroom: SmallMushroomModel,
  hedge_twisted: HedgeTwistedModel,
};
